import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { registerUserAccountInitiated, setMessage, registerFailedUserAccountSuccess, registerFailedUserAccountError } from '../../../store/user/user.actions'
import { Captcha, RegisterUserAccountRequest } from '@professocialApi/professocial-core-frontend-api'
import {
  RegistrationCustomerData
} from '@professocial/professocialTypes/types/commonTypes'
import RegisterForm, { IRegistrationForm } from './RegisterForm'
import Env from '../../../config/envConfig'
import { Alert, Box, Typography } from '@mui/material'
import { onFailedUserRegistration, registrationFailedSuccess } from '../../../store/user/user.selector'
import { EventType, Message, Severity } from '@professocial/professocialTypes/types/messageTypes'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import { useTranslation } from 'react-i18next'
import ButtonComponent from '@professocial/professocialFormComponents/FormComponents/ButtonComponent'
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft'

interface Props {
  isOpenCallback: (e: boolean) => void
  registrationCustomerData?: RegistrationCustomerData
}

const RegisterUser: React.FC<Props> = (props:Props) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const dispatchRegisterUserAccount = useCallback((params: RegisterUserAccountRequest) => dispatch(registerUserAccountInitiated(params)), [dispatch])
  const dispatchSetMessage = useCallback((params: Message) => dispatch(setMessage(params)), [dispatch])
  const dispatchRegisterFailedSuccess = useCallback(() => dispatch(registerFailedUserAccountSuccess()), [dispatch])
  const dispatchRegisterFailedError = useCallback((error: any) => dispatch(registerFailedUserAccountError(error)), [dispatch])

  const registrationData = props.registrationCustomerData
  const defaultScreenFields = registrationData?.fields
  const failedScreenFields = registrationData?.failed?.fields
  const loadingOnFailedUserRegistration = useSelector(onFailedUserRegistration)
  const isFailedRegistrationSuccessful = useSelector(registrationFailedSuccess)

  const [failedUserRegistration, setFailedUserRegistration] = useState(false)
  const [successfulUserRegistration, setSuccessfulUserRegistration] = useState(false)
  const [registeredEmail, setRegisteredEmail] = useState('')
  const [successfulFailedRegistration, setSuccessfulFailedRegistration] = useState(false)
  const [loadingOnUserRegistration, setLoadingOnUserRegistration] = useState(false)
  const [previousLoadingState, setPreviousLoadingState] = useState(false)

  // Track loadingOnFailedUserRegistration changes and check success state
  useEffect(() => {
    // If loading changed from true to false AND the registration was successful, show success message
    if (previousLoadingState && 
        !loadingOnFailedUserRegistration && 
        failedUserRegistration && 
        isFailedRegistrationSuccessful) {
      setTimeout(() => {
        setSuccessfulFailedRegistration(true)
      }, 100)
    }
    
    setPreviousLoadingState(loadingOnFailedUserRegistration)
  }, [loadingOnFailedUserRegistration, previousLoadingState, failedUserRegistration, isFailedRegistrationSuccessful])

  const onUserRegistration = (captcha: Captcha | undefined, data: IRegistrationForm) => {
    const xsrfToken = sessionStorage.getItem('XSRF-TOKEN')

    const requestHeaders: HeadersInit = new Headers()
    requestHeaders.set('Accept', 'application/json')
    requestHeaders.set('Content-Type', 'application/json')
    if (xsrfToken) requestHeaders.set('X-XSRF-TOKEN', xsrfToken)

    const basePath = Env.restBase
    const restUrl = `${basePath}/rest/professocial/core/gatekeeper/auth/new/register/${data.mandator}`

    if (captcha && data && data.mandator) {
      setLoadingOnUserRegistration(true)
      fetch(restUrl,
        {
          method: 'POST',
          credentials: 'include',
          headers: requestHeaders,
          body: JSON.stringify({
            username: data.username,
            email: data.username,
            password: data.password,
            captcha: { id: captcha.id || 0, solution: data.captcha },
          }),
        })
        .then(res => res.json())
        .then(json => {
          if (json.errorCode === 35004) {
            setFailedUserRegistration(true)
          }
          else if (json.errorCode === 80003) {
            dispatchSetMessage({
              severity: Severity.error,
              message: t('login:captchaFailed'),
              eventType: EventType.upload,
            })
          }
          else if (json.errorCode === 35006) {
            // ES-997: bewusst derselbe generische Text wie im Fallback unten (nicht
            // registrationData.usernameAlreadyExists) - ein abweichender Text würde
            // verraten, dass zu dieser E-Mail bereits ein Account existiert
            // (User-Enumeration). Nicht wieder spezifisch machen.
            dispatchSetMessage({
              severity: Severity.error,
              message: t('login:registrationFailed'),
              eventType: EventType.upload,
            })
          }
          else if (!json.errorCode) {
            setRegisteredEmail(data.username || '')
            setSuccessfulUserRegistration(true)
          }
          else {
            // Fallback für alle unbekannten Fehlercodes statt stillem Fehlschlag
            dispatchSetMessage({
              severity: Severity.error,
              message: t('login:registrationFailed'),
              eventType: EventType.upload,
            })
          }

          setLoadingOnUserRegistration(false)
        }).catch((error) => {
          setLoadingOnUserRegistration(false)
          dispatchSetMessage({
            severity: Severity.error,
            message: t('login:registrationFailed'),
            eventType: EventType.upload,
          })
        })
    }
  }

  const onUserFailedRegistration = (captcha: Captcha | undefined, data: IRegistrationForm) => {
    if (captcha && data.email && data.password) {
      const params: RegisterUserAccountRequest = {
        generalRegister: {
          email: data.email,
          agency: data.agency,
          password: data.password,
          firstname: data.firstname,
          lastname: data.lastname,
          motivation: data.motivation,
          homepage: data.homepage,
          captcha: { id: captcha.id || 0, solution: data.captcha },
        },
      }

      dispatchRegisterUserAccount(params)
    }
  }
  
  const handleBackToLogin = () => {
    props.isOpenCallback(false)
  }

  const successText = registrationData?.success?.replace('{{email}}', registeredEmail)

  return (
    <>
      {successfulUserRegistration && (
        <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
          <Alert
            severity='success'
            icon={<TaskAltIcon />}
            sx={{ mb: 3 }}
          >
            {successText}
          </Alert>
          <ButtonComponent
            variant='contained'
            startIcon={<KeyboardDoubleArrowLeftIcon />}
            onClick={handleBackToLogin}
            color='primary'
            text={'Zurück zum Login'}
          />
        </Box>
      )}
      {successfulFailedRegistration && (
        <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
          <Typography variant="h5" align="center">
            {registrationData?.failed?.successHeader}
          </Typography>
          <Alert
            severity='success'
            icon={<TaskAltIcon />}
            sx={{ mb: 3 }}
          >
            {registrationData?.failed?.success}
          </Alert>
          <ButtonComponent
            variant='contained'
            startIcon={<KeyboardDoubleArrowLeftIcon />}
            onClick={handleBackToLogin}
            color='primary'
            text={'Zurück zum Login'}
          />
        </Box>
      )}
      {!successfulUserRegistration && !successfulFailedRegistration && (
        <>
          {!failedUserRegistration && (
            <RegisterForm
              loading={loadingOnUserRegistration}
              headline={registrationData?.headline}
              fields={defaultScreenFields}
              isOpenCallback={props.isOpenCallback}
              callbackOnSubmit={onUserRegistration}
            />
          )}
          {failedUserRegistration && registrationData?.failed && (
            <RegisterForm
              loading={loadingOnFailedUserRegistration}
              headline={registrationData.failed.headline}
              fields={failedScreenFields}
              isOpenCallback={props.isOpenCallback}
              callbackOnSubmit={onUserFailedRegistration}
            />
          )}
        </>
      )}
    </>
  )
}

export default RegisterUser
